import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Removed static import - now using dynamic import
import { ExportPayload, FileFormat, ExportColumn, ExportOptions, ExportAction } from '../types';
import { useLoadingModal } from '../../../components';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { showWarningSnackbar } from '../../../components/snackBar/snakbar';

interface UseExportsProps {
  columns: ExportColumn[];
  action: ExportAction;
  startDate?: number | null;
  endDate?: number | null;
  options?: ExportOptions;
  accounttypeflag?: number | null;
}

export const useExports = ({ columns, action, startDate: propStartDate, endDate: propEndDate, options }: UseExportsProps) => {
  console.log('useExports initialized with action:', action);
  console.log(options, 'options');

  const [selected, setSelected] = useState<string[]>(columns.map((col) => col.accessor));
  const [selectedModule, setSelectedModule] = useState<FileFormat>('excel');
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
  const [errors, setErrors] = useState({ format: false, dateRange: false, tdstcsoption: false });
  const [startDateUrl, setStartDateUrl] = useState<string | null>(null);
  const [endDateUrl, setEndDateUrl] = useState<string | null>(null);
  const [statusFilters, setStatusFilters] = useState<{ active: boolean; cancelled: boolean }>({ active: true, cancelled: false });

  // When the columns list changes (e.g., switching TDS <-> TCS), reset selection to all columns.
  useEffect(() => {
    setSelected(columns.map((col) => col.accessor));
  }, [columns]);

  const {
    status: exportStatus,
    isOpen,
    executeWithLoading,
    close,
    downloadCallback,
    showEmptyExport,
    showLoading,
    showSuccess,
    showError,
  } = useLoadingModal();

  useEffect(() => {
    if ((options?.selectedArray?.length ?? 0) > 0) {
      setStatusFilters({
        active: true,
        cancelled: true,
      });
    }
  }, [options?.selectedArray]);

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const {
    startyear,
    endyear,
    startdate: fyStartDate,
    enddate: fyEndDate,
  } = useSelector((state: any) => state.userPrefrenceandReducer?.financialYear || {});

  // Priority: props > URL params > Financial year dates
  const startDate = useMemo(() => {
    if (startDateUrl !== null) {
      return startDateUrl;
    }

    if (propStartDate !== undefined) {
      return propStartDate !== null ? String(propStartDate) : null;
    }

    if (fyStartDate) {
      return String(fyStartDate);
    }

    return null;
  }, [startDateUrl, propStartDate, fyStartDate]);

  const endDate = useMemo(() => {
    if (endDateUrl !== null) {
      return endDateUrl;
    }

    if (propEndDate !== undefined) {
      return propEndDate !== null ? String(propEndDate) : null;
    }

    if (fyEndDate) {
      return String(fyEndDate);
    }

    return null;
  }, [endDateUrl, propEndDate, fyEndDate]);

  const HandleIncludeCancelled = (key: 'active' | 'cancelled', checked: boolean) => {
    setStatusFilters((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };
  console.log(options, 'options');

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(Number(startDate));
      const end = new Date(Number(endDate));
      setDateRange([start, end]);
    } else if (propStartDate !== undefined && propEndDate !== undefined) {
      // If props are explicitly set to null, clear date range
      if (propStartDate === null || propEndDate === null) {
        setDateRange(null);
      }
    }
  }, [startDate, endDate, propStartDate, propEndDate, fyStartDate, fyEndDate]);

  const handleChange = (accessor: string) => {
    setSelected((prev) => (prev.includes(accessor) ? prev.filter((item) => item !== accessor) : [...prev, accessor]));
  };

  const handleSelectAll = (columns: ExportColumn[]) => {
    const allAccessors = columns.map((col) => col.accessor);
    setSelected((prev) => (prev.length === allAccessors.length ? [] : allAccessors));
  };
  console.log(action, options, 'options');

  const filename = (startyear: string, endyear: string, dateRange: [Date, Date] | null, action: string) => {
    let dateToday = dayjs(Date.now()).format('DD-MM-YYYY');
    const startYear = startyear ? startyear : '';
    const endYear = endyear ? endyear : '';
    let fileType = options?.partyOption?.partyType
      ? (options?.partyOption?.partyType + '_export').toLowerCase()
      : options?.transactionOption?.transactionType
      ? (options?.transactionOption?.transactionType + '_export').toLowerCase()
      : action.replace(/EXPORT$/, '_export').toLowerCase();

    if (action === 'TCSEXPORT' || action === 'TDSEXPORT') {
      const option = params.get('option')?.toUpperCase() || '';
      const tdsTcsOption = option.includes('PAY') ? 'payable' : 'receivable';
      fileType = action.replace(/EXPORT$/, `${tdsTcsOption}_export`).toLowerCase();
    }

    return `${fileType}_${startYear}-${endYear.toString().slice(-2)}FY_${dateToday}`;
  };

  const handleDateRangeChange = (value: [Date, Date] | null) => {
    setDateRange(value);
    if (value) {
      const [start, end] = value;
      const startTimestamp = Math.floor(start.getTime());
      const endTimestamp = Math.floor(end.getTime());

      const newParams = new URLSearchParams(location.search);
      newParams.set('startDate', startTimestamp.toString());
      newParams.set('endDate', endTimestamp.toString());

      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
      setErrors((prev) => ({ ...prev, dateRange: false }));
    } else {
      setErrors((prev) => ({ ...prev, dateRange: true }));
      setDateRange(null);
    }
  };

  const readUrlDates = useCallback(() => {
    const locationParams = new URLSearchParams(location.search);
    const urlStartDate = locationParams.get('startDate');
    const urlEndDate = locationParams.get('endDate');

    setStartDateUrl(urlStartDate);
    setEndDateUrl(urlEndDate);
  }, [location.search]);

  useEffect(() => {
    readUrlDates();
  }, [readUrlDates]);

  const handleModuleChange = (value: FileFormat | undefined) => {
    if (value) {
      setSelectedModule(value);
      setErrors((prev) => ({ ...prev, format: false }));
    }
  };

  const handleExport = async () => {
    // Check if TDS/TCS option is required and empty
    const isTdsTcsExport = action === 'TDSEXPORT' || action === 'TCSEXPORT';
    const isTdsTcsOptionEmpty =
      isTdsTcsExport && (!options?.tdstcsoption || options.tdstcsoption === null || options.tdstcsoption === undefined);

    const hasAtLeastOneStatusSelected = statusFilters.active || statusFilters.cancelled;

    if (!hasAtLeastOneStatusSelected) {
      showWarningSnackbar('Select atleast one status (Active or Cancelled)');
      return;
    }

    const newErrors = {
      format: !selectedModule,
      dateRange: !startDate || !endDate,
      tdstcsoption: isTdsTcsOptionEmpty,
    };

    setErrors(newErrors);

    if (newErrors.format || newErrors.dateRange || newErrors.tdstcsoption) {
      return;
    }

    try {
      showLoading();

      const [{ defaultExportStyles }, { validateExportPayload }, exportService] = await Promise.all([
        import('../data/exportData'),
        import('../types'),
        import('../services/ExportService'),
      ]);

      const { getExportData, downloadFile } = exportService;

      const payload: ExportPayload = {
        action,
        fromDate: Number(startDate!),
        toDate: Number(endDate!),
        fileType: selectedModule!,
        formatting: {
          headerBg: '#E6CED7',
          bodyBg: '',
          styles: defaultExportStyles,
          defaultHeaderStyle: 'header',
          defaultCellStyle: 'body',
        },
        columns: columns.filter((col) => selected.includes(col.accessor)),
      };


      console.log(payload,"!!!!payloadddd")

      const getIncludeCancelValue = (statusFilters: { active: boolean; cancelled: boolean }) => {
        const { active, cancelled } = statusFilters;

        if (active && cancelled) return 'Both';
        if (active) return 'Active';
        if (cancelled) return 'Cancelled';
      };

      // Add options if provided
      if (options) {
        payload.options = {
          ...(options.tdstcsoption && { tdstcsoption: options.tdstcsoption }),
          ...(options.transactionOption && { transactionOption: options.transactionOption }),
          ...(options.ledgerOptions && { ledgerOptions: options.ledgerOptions }),
          ...(options.partyOption && { partyOption: options.partyOption, status: options.status }),
          ...(options.status && { status: options.status }),
          ...(options.crDrOption && options),
          ...(options.selectedArray && { selectedArray: options.selectedArray, includeStatus: getIncludeCancelValue(statusFilters) }),
          ...(options.filters && { filters: options.filters }),
        };
      }

      console.log('Export Debug Info:');
      console.log('Action:', action);
      console.log('Options:', options);
      console.log('Payload:', payload);

      // Validate payload before sending
      const validationErrors = validateExportPayload(payload);
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.map((err) => `${err.field}: ${err.message}`).join('\n');
        throw new Error(`Export validation failed:\n${errorMessage}`);
      }

      const result = await getExportData(payload, filename(startyear, endyear, dateRange, payload.action));

      if (result.isEmptyExport) {
        // Show empty export modal with download option
        showEmptyExport(() => {
          downloadFile(result.blob, result.filename);
        });
        // Don't call showSuccess() - let the empty export modal handle the UI
      } else {
        // Auto-download as usual and show success
        downloadFile(result.blob, result.filename);
        showSuccess();
      }
    } catch (error) {
      showError();
      throw error;
    }
  };

  return {
    selected,
    selectedModule,
    setSelectedModule: handleModuleChange,
    startDate,
    endDate,
    dateRange,
    errors,
    exportStatus,
    isOpen,
    downloadCallback,
    handleChange,
    handleSelectAll,
    handleDateRangeChange,
    handleExport,
    handleCloseModal: close,
    HandleIncludeCancelled,
    statusFilters,
  };
};
